// Help > Escalate to on-call support (KAT-8489). Pages fastn's on-call engineer through
// Spike, so the form makes the consequence explicit (a confirm box the customer
// has to tick) and only shows a reference once the server says Spike accepted it.
// The receipt opens Messenger on the ticket the engineer answers in, so the
// customer does not have to find it under Contact support.

import React, { useState } from "react";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { OPEN_CONVERSATION_EVENT, OPEN_LIVE_CHAT_EVENT, OPEN_TICKET_EVENT } from "@/components/IntercomWidget";
import {
  useCreateSupportEscalation,
  type EscalationImpact,
  type SupportEscalationReceipt,
} from "@/hooks/use-support-escalation";
import { urgentEscalationTestIds as t } from "@/testids/urgentEscalationTestIds";

const IMPACTS: Array<{ value: EscalationImpact; label: string }> = [
  { value: "outage", label: "Production is down" },
  { value: "degraded", label: "Production is degraded" },
  { value: "data", label: "Data is wrong or missing" },
  { value: "security", label: "Security concern" },
];

// Sent as the label: the engineer reads it next to the customer's time zone and
// the time the page was raised, which beats a guessed clock time in a box.
const STARTED = ["Just now", "In the last hour", "Earlier today", "Before today"] as const;

const PHONE_RE = /^\+?[0-9 ()\-.]{6,40}$/;

/** Where the customer was, without query or fragment (those can carry tokens). */
function currentPage(): string {
  return `${window.location.origin}${window.location.pathname}`.slice(0, 500);
}

function customerTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

export function UrgentEscalationDialog({ onClose }: { onClose: () => void }): React.ReactElement {
  // One key per opening of the dialog, reused if the customer retries after an
  // error, so the server can tell a retry from a second escalation.
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const [summary, setSummary] = useState("");
  // "" = not chosen. Optional: a customer who cannot tell yet should still be able to page.
  const [impact, setImpact] = useState<EscalationImpact | "">("");
  const [details, setDetails] = useState("");
  const [startedAt, setStartedAt] = useState("");
  const [affected, setAffected] = useState("");
  const [phone, setPhone] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [receipt, setReceipt] = useState<SupportEscalationReceipt | null>(null);
  const create = useCreateSupportEscalation();
  const sending = create.isPending;

  const summaryError = summary.trim().length > 0 && summary.trim().length < 5 ? "At least 5 characters." : undefined;
  const detailsLength = details.trim().length;
  const detailsError =
    detailsLength > 0 && detailsLength < 10 ? `At least 10 characters (${detailsLength}/10).` : undefined;
  const phoneError = phone.trim() && !PHONE_RE.test(phone.trim()) ? "Digits, spaces and + ( ) - only." : undefined;
  // The first thing still stopping the send, said next to the disabled button so
  // nobody is left guessing why it will not press.
  const missing =
    summary.trim().length < 5
      ? "Add a summary (5+ characters)."
      : detailsLength < 10
        ? "Add details (10+ characters)."
        : phoneError
          ? "Fix the callback number."
          : !confirmed
            ? "Tick the box above to send."
            : null;
  const ready = !missing && !sending;

  // While the page is in flight the dialog cannot be dismissed: closing it would
  // hide whether anyone was paged.
  const close = () => {
    if (!sending) onClose();
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    try {
      const r = await create.mutateAsync({
        summary: summary.trim(),
        ...(impact ? { impact } : {}),
        details: details.trim(),
        idempotencyKey,
        ...(startedAt.trim() ? { startedAt: startedAt.trim() } : {}),
        ...(affected.trim() ? { affected: affected.trim() } : {}),
        ...(phone.trim() ? { callbackPhone: phone.trim() } : {}),
        pageUrl: currentPage(),
        ...(customerTimeZone() ? { timezone: customerTimeZone() } : {}),
      });
      setReceipt(r);
    } catch {
      // Shown from create.error below.
    }
  };

  const openThread = (r: SupportEscalationReceipt) => {
    onClose();
    window.dispatchEvent(
      r.ticketId
        ? new CustomEvent(OPEN_TICKET_EVENT, { detail: r.ticketId })
        : r.conversationId
          ? new CustomEvent(OPEN_CONVERSATION_EVENT, { detail: r.conversationId })
          : new Event(OPEN_LIVE_CHAT_EVENT),
    );
  };

  return (
    <Dialog open onClose={close} labelledBy="urgent-escalation-title" data-testid={t.modal} className="max-w-lg">
      <DialogCloseButton onClick={close} disabled={sending} data-testid={t.closeButton} />
      {receipt ? (
        <>
          <DialogHeader>
            <DialogTitle id="urgent-escalation-title">Escalated to our on-call engineer</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className="flex flex-col gap-3" data-testid={t.successPanel}>
            <p className="text-sm text-fg-body">
              We have escalated this to our on-call engineer. Quote this reference if you follow up:
            </p>
            <p className="font-mono text-sm text-fg" data-testid={t.incidentRef}>
              {receipt.escalationId}
            </p>
            <p className="text-sm text-fg-muted" data-testid={t.followUpText}>
              {receipt.ticketId
                ? "We opened a support ticket for this. The engineer will reply there, and you can follow its status. Open it to add screenshots, error messages or anything new."
                : receipt.conversationId
                  ? "The engineer will reply in your support conversation. Open it to add screenshots, error messages or anything new."
                  : "To add anything, message us from Contact support and mention the reference."}
            </p>
            <p className="text-sm text-fg-body" data-testid={t.availabilityText}>
              So we can keep treating this as urgent, please stay available: reply to the engineer and join the call
              if they share a meeting link. If we are unable to reach you, we will continue at normal priority. Thank
              you.
            </p>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={onClose} data-testid={t.doneButton}>
              Done
            </Button>
            <Button onClick={() => openThread(receipt)} data-testid={t.openConversationButton}>
              {receipt.ticketId ? "Open ticket" : receipt.conversationId ? "Open conversation" : "Open Contact support"}
            </Button>
          </DialogFooter>
        </>
      ) : (
        <form onSubmit={submit} data-testid={t.form}>
          <DialogHeader>
            <DialogTitle id="urgent-escalation-title">Escalate to on-call support</DialogTitle>
            <DialogDescription>
              This pages fastn&apos;s on-call engineer straight away, day or night. Use it when production is
              affected. For anything else, use Contact support.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-3">
            <Input
              label="Summary"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              maxLength={200}
              placeholder="All order-sync runs stuck in Queued"
              error={summaryError}
              required
              disabled={sending}
              data-testid={t.summaryInput}
            />
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-fg-body">Impact (optional)</span>
              <Select
                value={impact}
                onChange={(e) => setImpact(e.target.value as EscalationImpact | "")}
                disabled={sending}
                data-testid={t.impactDropdown}
              >
                <option value="">Not sure</option>
                {IMPACTS.map((i) => (
                  <option key={i.value} value={i.value}>
                    {i.label}
                  </option>
                ))}
              </Select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-fg-body">Details</span>
              <Textarea
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                maxLength={4000}
                rows={4}
                placeholder="What fails, any error message, and who is affected."
                required
                disabled={sending}
                aria-invalid={detailsError ? true : undefined}
                aria-describedby={detailsError ? "urgent-escalation-details-error" : undefined}
                className={detailsError ? "border-error" : undefined}
                data-testid={t.detailsTextarea}
              />
              {detailsError ? (
                <span id="urgent-escalation-details-error" role="alert" className="text-xs text-error">
                  {detailsError}
                </span>
              ) : null}
            </label>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-fg-body">When did it start? (optional)</span>
                <Select
                  value={startedAt}
                  onChange={(e) => setStartedAt(e.target.value)}
                  disabled={sending}
                  data-testid={t.startedDropdown}
                >
                  <option value="">Not sure</option>
                  {STARTED.map((label) => (
                    <option key={label} value={label}>
                      {label}
                    </option>
                  ))}
                </Select>
              </label>
              <Input
                label="Callback number (optional)"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                maxLength={40}
                placeholder="+1 415 555 0100"
                error={phoneError}
                disabled={sending}
                data-testid={t.phoneInput}
              />
            </div>
            <Input
              label="Affected workflow, connector or customer (optional)"
              value={affected}
              onChange={(e) => setAffected(e.target.value)}
              maxLength={300}
              disabled={sending}
              data-testid={t.affectedInput}
            />
            <p className="text-xs text-fg-muted" data-testid={t.nextStepsText}>
              What happens next: we page our on-call engineer now and open a support ticket for you in Contact
              support. The engineer replies there, and calls you if you leave a number. So we can treat this as
              urgent, please stay available while we work on it: reply when the engineer messages you, and join the
              call if they share a meeting link. If we are unable to reach you, we will continue at normal priority.
            </p>
            <label className="flex items-start gap-2 text-sm text-fg-body">
              <Checkbox
                checked={confirmed}
                onCheckedChange={setConfirmed}
                disabled={sending}
                className="mt-0.5"
                aria-label="I understand this pages the on-call engineer now, and I'll stay available to respond"
                data-testid={t.confirmCheckbox}
              />
              <span>I understand this calls fastn&apos;s on-call engineer now, and I&apos;ll stay available to respond.</span>
            </label>
            {create.error ? (
              <Alert tone="error" data-testid={t.errorMessage}>
                {create.error.message}
              </Alert>
            ) : null}
          </DialogBody>
          <DialogFooter>
            {missing && !sending ? (
              <span className="mr-auto text-xs text-fg-muted" data-testid={t.missingHint}>
                {missing}
              </span>
            ) : null}
            <Button type="button" variant="ghost" onClick={close} disabled={sending} data-testid={t.cancelButton}>
              Cancel
            </Button>
            <Button type="submit" disabled={!ready} data-testid={t.submitButton}>
              {sending ? "Paging on-call…" : "Page on-call engineer"}
            </Button>
          </DialogFooter>
        </form>
      )}
    </Dialog>
  );
}
